import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { LoadManager } from './custom-collection-utility.js';

/**
 * @typedef {Object} QuickFiltersRefs
 * @property {HTMLElement[]} quickFilterItems - The quick filter item elements
 */

/**
 * Handles quick filter interactions with improved selector logic and visual feedback
 * 
 * This component automatically works with the new URL-less filter system:
 * - When facets-form-component has `disable-url-updates` attribute: Uses URL-less mode
 * - When no attribute is set: Uses normal mode with URL updates
 * - All filter updates go through the smart updateFilters() method which auto-detects preferences
 * 
 * @extends {Component<QuickFiltersRefs>}
 */
export class QuickFiltersComponent extends Component {

  /** @type {HTMLElement | null | undefined} */
  #recommendationOverlay;
  /** @type {HTMLElement | null | undefined} */
  #recommendationStartButton;
  /** @type {HTMLElement | null | undefined} */
  #recommendationCloseButton;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.quickFilterItems = this.querySelectorAll('.quick-filter-item');
    this.#recommendationOverlay =
    /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendation-overlay]'));
    this.#recommendationStartButton =
    /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendation-start]'));
    this.#recommendationCloseButton =
    /** @type {HTMLElement | null} */ (this.querySelector('[data-recommendation-close]'));

    if (this.#recommendationStartButton instanceof HTMLElement) {
      this.#recommendationStartButton.addEventListener('click', () => {
        this.#toggleRecommendationOverlay(false);
        this.toggleRecommendedFilter();
      });
    }

    // if you click on the close button, close the overlay
    if (this.#recommendationCloseButton instanceof HTMLElement) {
      this.#recommendationCloseButton.addEventListener('click', () => {
        this.#toggleRecommendationOverlay(false);
      });
    }

    this.#init();

    // Listen for custom sync event
    document.addEventListener('sync-quick-filters', this.#handleSyncEvent);
    // Listen for filter updates to sync person toggle options
    // document.addEventListener('sync-quick-filters', this.#syncPersonToggleOptions);

    // Listen for recommendations updated event
    // window.addEventListener('ecomm-recommendations-updated', this.#handleRecommendationsUpdated);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#syncQuickFilterStates);
    document.removeEventListener('sync-quick-filters', this.#handleSyncEvent);
    // document.removeEventListener('sync-quick-filters', this.#syncPersonToggleOptions);
  }

  /**
   * Initialize the quick filters component
   */
  #init() {
    if (this.quickFilterItems?.length === 0) return;

    this.quickFilterItems?.forEach(quickFilter => {
      quickFilter.addEventListener('click', this.#handleQuickFilterClick.bind(this));
      quickFilter.addEventListener('keydown', this.#handleQuickFilterKeydown.bind(this));
    });

    // Listen for filter changes to update quick filter states
    document.addEventListener(ThemeEvents.FilterUpdate, this.#syncQuickFilterStates);

    // Initialize quick filter states based on current filter states
    this.#syncQuickFilterStates();
  }

  /**
   * Synchronizes quick filter visual states with actual filter states
   */
  #syncQuickFilterStates = () => {
    const quickFilterItems = document.querySelectorAll('.quick-filter-item');
    // Check if any filters are active in the filters drawer if not make "Alle" appear selected.
    // const filtersDrawer = document.querySelector('#filters-drawer');
    // const hasAnyActiveFilter =
    //   filtersDrawer instanceof HTMLElement
    //     ? Boolean(filtersDrawer.querySelector('input[type="checkbox"]:checked'))
    //     : false;


    quickFilterItems.forEach(quickFilter => {
      if (quickFilter instanceof HTMLElement) {
        const filterId = quickFilter.getAttribute('data-filter-id');

        if (filterId) {
          const filterInput = this.#findFilterInput(filterId);

          if (filterInput) {
            this.#updateQuickFilterVisualState(quickFilter, filterInput.checked);
          } else {
            // If filter input not found, ensure quick filter is in inactive state
            this.#updateQuickFilterVisualState(quickFilter, false);
          }
        }
      }
    });

    // Default state: if no filters are active, make "Alle" appear selected.
    // This keeps the UX consistent ("show all" is the default) without requiring a filter update.
    // if (!hasAnyActiveFilter) {
    //   const alleQuickFilters = document.querySelectorAll('.quick-filter-item[data-filter-id="Alle"]');
    //   alleQuickFilters.forEach((alleQuickFilter) => {
    //     if (!(alleQuickFilter instanceof HTMLElement)) return;
    //     this.#updateQuickFilterVisualState(alleQuickFilter, true);
    //   });

    //   const alleInput = this.#findFilterInput('Alle');
    //   if (alleInput && !alleInput.checked) {
    //     alleInput.checked = true;
    //   }
    // }
  };


  /**
   * Handles the custom sync event
   */
  #handleSyncEvent = () => {
    this.#syncQuickFilterStates();
  };

  /**
   * Handles quick filter click events
   * @param {Event} event - The click event
   */
  async #handleQuickFilterClick(event) {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const filterId = event.currentTarget.getAttribute('data-filter-id');

    // If "Aanbevolen" is currently active and the user clicks any OTHER quick filter,
    // switch to "Alle", uncheck "Aanbevolen", and keep the clicked filter selected.
    // (We do a single filter update and skip the normal toggle behavior.)
    // if (filterId && filterId !== 'Aanbevolen' && filterId !== 'Alle') {
    //   const aanbevolenInput = this.#findFilterInput('Aanbevolen');
    //   if (aanbevolenInput?.checked) {
    //     const alleInput = this.#findFilterInput('Alle');
    //     const clickedInput = this.#findFilterInput(filterId);

    //     // If we can't find the clicked input, fall back to normal behavior.
    //     if (!clickedInput) return;

    //     let didChange = false;

    //     if (alleInput && !alleInput.checked) {
    //       alleInput.checked = true;
    //       didChange = true;
    //     }

    //     if (aanbevolenInput.checked) {
    //       aanbevolenInput.checked = false;
    //       didChange = true;
    //     }

    //     if (!clickedInput.checked) {
    //       clickedInput.checked = true;
    //       didChange = true;
    //     }

    //     if (didChange) {
    //       await this.#triggerFilterUpdate(clickedInput);
    //     }

    //     return;
    //   }
    // }

    if (filterId === 'Aanbevolen') {
      this.#toggleRecommendationOverlay();
      return;
      // if (!sessionStorage.getItem('recommendations')) {
      //   event.preventDefault();
      //   this.#toggleRecommendationOverlay();
      //   return;
      // }
      // else {
      //   const aanbevolenInput = this.#findFilterInput('Aanbevolen');
      //   if (aanbevolenInput && !aanbevolenInput.checked) {
      //     LoadManager.addLoader();
      //     window.setTimeout(() => {
      //       LoadManager.removeLoader();
      //     }, 2500);
      //   }

      //   // If "Aanbevolen" is clicked while any other quick filter is active,
      //   // clear those other quick filters (only the ones shown as quick filters).
      //   this.#clearOtherQuickFilters('Aanbevolen');
      // }
    }

    // Simple mutual exclusivity: uncheck the other filter if needed
    // if (filterId === 'Aanbevolen') {
    //   const alleInput = this.#findFilterInput("Alle");
    //   if (alleInput?.checked) {
    //     alleInput.checked = false;
    //   }
    // }

    // if (filterId === 'Alle') {
    //   const aanbevolenInput = this.#findFilterInput("Aanbevolen");
    //   if (aanbevolenInput?.checked) {
    //     aanbevolenInput.checked = false;
    //   }
    // }

    // Toggle the clicked filter
    await this.#toggleQuickFilter(event.currentTarget);
  }

  // #handleRecommendationsUpdated = async () => {
  //   const aanbevolenInput = this.#findFilterInput('Aanbevolen');
  //   const alleInput = this.#findFilterInput("Alle");

  //   if (alleInput?.checked) {
  //     alleInput.checked = false;
  //   }

  //   if (aanbevolenInput) {
  //     aanbevolenInput.checked = true;
  //     await this.#triggerFilterUpdate(aanbevolenInput);
  //   }
  // }

  // #syncPersonToggleOptions = () => {
  //   // Get the recommendations payload
  //   const recommendationsPayload = sessionStorage.getItem('recommendations');
  //   if (!recommendationsPayload) return;

  //   try {
  //     const payload = JSON.parse(recommendationsPayload);
  //     const profiles = Array.isArray(payload?.profiles) ? payload.profiles : [];


  //     // Find the person toggle options container
  //     const toggleOptionsElement = document.querySelector("[data-ecomm-person-toggle-options]");
  //     if (!(toggleOptionsElement instanceof HTMLElement)) return;

  //     const slotElements = Array.from(toggleOptionsElement.querySelectorAll('[data-ecomm-profile-slot]')).filter(
  //       (el) => el instanceof HTMLElement
  //     );

  //     slotElements.forEach((slotElement, index) => {
  //       if (!(slotElement instanceof HTMLElement)) return;

  //       const profile = profiles[index];
  //       const inputElement = slotElement.querySelector('input[name="ecomm-profile"]');
  //       const labelElement = slotElement.querySelector('label[data-ecomm-profile-label]');
  //       if (!(inputElement instanceof HTMLInputElement) || !(labelElement instanceof HTMLElement)) return;

  //       if (!profile) {
  //         slotElement.classList.add('no-disp');
  //         return;
  //       }

  //       slotElement.classList.remove('no-disp');


  //     });

  //     // Hide the entire toggle if there's only one profile or less
  //     const toggleElement = document.querySelector('[data-ecomm-person-toggle]');
  //     if (toggleElement instanceof HTMLElement) {
  //       if (profiles.length <= 1) {
  //         toggleElement.classList.add('no-disp');
  //       } else {
  //         toggleElement.classList.remove('no-disp');
  //       }
  //     }

  //     // Initialize person toggle logger
  //     const windowAny = /** @type {any} */ (window);
  //     const recommendationsUtils = windowAny.EcommRecommendationsUtils;
  //     if (recommendationsUtils && typeof recommendationsUtils.initPersonToggleLogger === 'function') {
  //       recommendationsUtils.initPersonToggleLogger(toggleOptionsElement);
  //     }
  //   } catch (error) {
  //     console.error("Error syncing person toggle options", error);
  //   }
  // }

  /**
   * Handles quick filter keyboard events
   * @param {KeyboardEvent} event - The keyboard event
   */
  async #handleQuickFilterKeydown(event) {
    // Activate on Enter or Space key
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!(event.currentTarget instanceof HTMLElement)) return;

      const filterId = event.currentTarget.getAttribute('data-filter-id');

      if (filterId === 'Aanbevolen') {
        this.#toggleRecommendationOverlay();
        return;
      }

      // Match click behavior: if "Aanbevolen" is active and a different quick filter is activated,
      // switch to "Alle", uncheck "Aanbevolen", and keep the activated filter selected.
      // if (filterId && filterId !== 'Alle') {
      //   const aanbevolenInput = this.#findFilterInput('Aanbevolen');
      //   if (aanbevolenInput?.checked) {
      //     const clickedInput = this.#findFilterInput(filterId);
      //     if (clickedInput) {
      //       const alleInput = this.#findFilterInput('Alle');

      //       let didChange = false;
      //       if (alleInput && !alleInput.checked) {
      //         alleInput.checked = true;
      //         didChange = true;
      //       }
      //       if (aanbevolenInput.checked) {
      //         aanbevolenInput.checked = false;
      //         didChange = true;
      //       }
      //       if (!clickedInput.checked) {
      //         clickedInput.checked = true;
      //         didChange = true;
      //       }

      //       if (didChange) {
      //         await this.#triggerFilterUpdate(clickedInput);
      //       }

      //       return;
      //     }
      //   }
      // }

      await this.#toggleQuickFilter(event.currentTarget);
    }
  }

  toggleRecommendedFilter() {

    // Find the survey form component and open it (non-intrusive to existing logic)
    const surveyForm = document.querySelector('thuysvers-survey-form-component');
    if (surveyForm && typeof /** @type {any} */ (surveyForm).showSurveyForm === 'function') {
      /** @type {any} */ (surveyForm).showSurveyForm();
    } else if (surveyForm instanceof HTMLElement) {
      // Fallback: simply remove the hidden class if method isn't available
      surveyForm.classList.remove('no-disp');
      document.documentElement.setAttribute('scroll-lock', '');
    }
  }

  resetRecommendedFilter() {
    this.#toggleRecommendationOverlay(false);
  }

  /**
   * Toggles a quick filter's state
   * @param {HTMLElement} quickFilter - The quick filter element
   */
  async #toggleQuickFilter(quickFilter) {
    const filterId = quickFilter.getAttribute('data-filter-id');

    if (!filterId) {
      // console.warn('Quick filter missing data-filter-id attribute');
      return;
    }

    // Find the corresponding filter input across all forms
    const filterInput = this.#findFilterInput(filterId);

    if (filterInput) {
      // Toggle the filter state
      const wasChecked = filterInput.checked;
      filterInput.checked = !wasChecked;

      // Trigger the filter update through the proper form component and wait for completion
      await this.#triggerFilterUpdate(filterInput);

      // Note: Quick filter states will be synced automatically after section rendering completes
    } else {
      // console.warn(`No filter input found for quick filter: ${filterId}`);
    }
  }

  /**
   * Finds filter input across all available forms
   * @param {string} filterId - The filter identifier
   * @returns {HTMLInputElement|null} The filter input element
   */
  #findFilterInput(filterId) {
    // Try multiple selector strategies for maximum compatibility
    const selector = `input[data-label="${filterId}"]`;

    // Search in all possible form contexts
    const formContext = '#filters-drawer';

    const input = document.querySelector(`${formContext} ${selector}`);
    if (input instanceof HTMLInputElement) {
      return input;
    }

    return null;
  }

  /**
   * Unchecks inputs corresponding to other quick filter items.
   * This ensures "Aanbevolen" can behave like a single-select quick filter.
   * @param {string} keepFilterId - The quick filter id to keep as-is
   */
  // #clearOtherQuickFilters(keepFilterId) {
  //   const quickFilterItems = document.querySelectorAll('.quick-filter-item');

  //   quickFilterItems.forEach((quickFilter) => {
  //     if (!(quickFilter instanceof HTMLElement)) return;
  //     const id = quickFilter.getAttribute('data-filter-id');
  //     if (!id || id === keepFilterId) return;

  //     const input = this.#findFilterInput(id);
  //     if (input?.checked) {
  //       input.checked = false;
  //     }
  //   });
  // }

  /**
   * Triggers filter update through the form component
   * @param {HTMLInputElement} filterInput - The filter input that was changed
   * @returns {Promise<void>} Promise that resolves when filter update is complete
   */
  async #triggerFilterUpdate(filterInput) {
    // Find the parent facets form component
    const facetsFormComponent = filterInput.closest('facets-form-component');

    if (facetsFormComponent && 'updateFilters' in facetsFormComponent && typeof facetsFormComponent.updateFilters === 'function') {
      // Check if URL updates are disabled for logging purposes
      const disableURLUpdates = facetsFormComponent.hasAttribute && facetsFormComponent.hasAttribute('disable-url-updates') ||
        (/** @type {any} */ (facetsFormComponent).disableURLUpdates !== undefined && /** @type {any} */ (facetsFormComponent).disableURLUpdates);

      if (disableURLUpdates) {
        // console.log('🔍 [QUICK-FILTERS] Using URL-less filter update mode');
      } else {
        // console.log('🔍 [QUICK-FILTERS] Using normal filter update mode with URL updates');
      }

      // updateFilters is now properly async and returns a Promise
      // It automatically detects URL update preferences and uses the appropriate method
      await facetsFormComponent.updateFilters();

      // console.log('✅ [QUICK-FILTERS] Filter update completed successfully');
    } else {
      // Fallback: dispatch change event to trigger any listeners
      // console.warn('⚠️ [QUICK-FILTERS] No facets form component found, using fallback method');
      filterInput.dispatchEvent(new Event('change', { bubbles: true }));
      // Small delay for any synchronous handlers
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Updates visual state of quick filter
   * @param {HTMLElement} quickFilter - The quick filter element
   * @param {boolean} isActive - Whether the filter is active
   */
  #updateQuickFilterVisualState(quickFilter, isActive) {
    if (isActive) {
      quickFilter.classList.add('quick-filter-item--active');
      quickFilter.setAttribute('aria-pressed', 'true');
    } else {
      quickFilter.classList.remove('quick-filter-item--active');
      quickFilter.setAttribute('aria-pressed', 'false');
    }
  }

  /**
   * Toggles the recommendation overlay visibility
   * @param {boolean} [forceVisible] - Optional flag to force visibility on/off
   */
  #toggleRecommendationOverlay(forceVisible) {
    if (!(this.#recommendationOverlay instanceof HTMLElement)) return;

    const isCurrentlyVisible = this.#recommendationOverlay.classList.contains('quick-filters-recommendation-overlay--visible');
    const shouldShow = typeof forceVisible === 'boolean' ? forceVisible : !isCurrentlyVisible;

    if (shouldShow) {
      this.#recommendationOverlay.classList.add('quick-filters-recommendation-overlay--visible');
      this.#recommendationOverlay.setAttribute('aria-hidden', 'false');

      if (this.#recommendationStartButton instanceof HTMLElement) {
        this.#recommendationStartButton.focus();
      }
    } else {
      this.#recommendationOverlay.classList.remove('quick-filters-recommendation-overlay--visible');
      this.#recommendationOverlay.setAttribute('aria-hidden', 'true');
    }
  }

}

if (!customElements.get('quick-filters-component')) {
  customElements.define('quick-filters-component', QuickFiltersComponent);
}